import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { Inbox, Users, Mail, Phone, Video, ChevronDown } from "lucide-react";

const statusColors = {
  new: "border-[#D4AF37] text-[#D4AF37]",
  contacted: "border-[#3B82F6] text-[#3B82F6]",
  closed: "border-[#10B981] text-[#10B981]",
};
const contactIcon = { Email: Mail, Phone: Phone, "Video Call": Video };

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/dashboard");
  }, [user, loading, navigate]);

  const load = () => {
    Promise.all([api.get("/inquiries"), api.get("/admin/stats")])
      .then(([i, s]) => { setInquiries(i.data); setStats(s.data); })
      .catch(() => {})
      .finally(() => setFetching(false));
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/inquiries/${id}`, { status });
      setInquiries((list) => list.map((q) => (q.id === id ? { ...q, status } : q)));
      api.get("/admin/stats").then((s) => setStats(s.data)).catch(() => {});
      toast.success(`Marked as ${status}`);
    } catch { toast.error("Update failed"); }
  };

  if (loading || !user || user.role !== "admin") {
    return <div className="min-h-screen grid place-items-center bg-[#0B0C10]"><div className="h-10 w-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const statCards = [
    { label: "Total Inquiries", value: stats?.total_inquiries ?? "—", icon: Inbox },
    { label: "New / Unhandled", value: stats?.new_inquiries ?? "—", icon: Mail },
    { label: "Members", value: stats?.members ?? "—", icon: Users },
  ];

  return (
    <div className="grain bg-[#0B0C10] min-h-screen">
      <Navbar />
      <section className="pt-32 pb-10 px-6 md:px-12 lg:px-24 border-b border-[#1F222E]">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Admin</p>
        <h1 className="font-display text-4xl sm:text-5xl text-white font-light mt-4">Client Inquiries</h1>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
          {statCards.map((s) => (
            <div key={s.label} className="border border-[#1F222E] bg-[#12141A] p-6 rounded-sm" data-testid={`stat-${s.label.split(" ")[0].toLowerCase()}`}>
              <s.icon className="text-[#D4AF37]" size={22} />
              <p className="text-3xl font-display text-white mt-4">{s.value}</p>
              <p className="text-sm text-[#9CA3AF] mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {fetching ? (
          <p className="text-[#9CA3AF]">Loading inquiries…</p>
        ) : inquiries.length === 0 ? (
          <div className="border border-[#1F222E] bg-[#12141A] p-16 text-center" data-testid="admin-empty">
            <Inbox className="text-[#6B7280] mx-auto" size={36} />
            <p className="text-[#9CA3AF] mt-4">No inquiries yet. Submissions from the website will appear here.</p>
          </div>
        ) : (
          <div className="space-y-px bg-[#1F222E] border border-[#1F222E]" data-testid="inquiries-list">
            {inquiries.map((q) => {
              const CIcon = contactIcon[q.preferred_contact] || Mail;
              const open = expanded === q.id;
              return (
                <div key={q.id} className="bg-[#12141A]" data-testid={`inquiry-row-${q.id}`}>
                  <button onClick={() => setExpanded(open ? null : q.id)} className="w-full p-6 flex items-center gap-4 text-left hover:bg-[#16181f] transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-white font-medium">{q.full_name}</span>
                        {q.business_name && <span className="text-sm text-[#6B7280]">· {q.business_name}</span>}
                        <span className={`text-xs uppercase tracking-wider px-2.5 py-0.5 border rounded-sm ${statusColors[q.status]}`}>{q.status}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-[#9CA3AF] flex-wrap">
                        <span className="truncate">{q.email}</span>
                        <span className="flex items-center gap-1.5 text-xs"><CIcon size={13} /> {q.preferred_contact}</span>
                        <span className="text-xs text-[#6B7280]">{q.business_stage}</span>
                      </div>
                    </div>
                    <ChevronDown size={18} className={`text-[#6B7280] transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="px-6 pb-6 pt-2 border-t border-[#1F222E] grid sm:grid-cols-2 gap-6" data-testid={`inquiry-detail-${q.id}`}>
                      <div className="space-y-3 text-sm">
                        {q.phone && <p className="text-[#9CA3AF]"><span className="text-[#6B7280]">Phone:</span> {q.phone}</p>}
                        {q.industry && <p className="text-[#9CA3AF]"><span className="text-[#6B7280]">Industry:</span> {q.industry}</p>}
                        <p className="text-[#9CA3AF]"><span className="text-[#6B7280]">Services:</span> {q.services?.join(", ") || "—"}</p>
                        <p className="text-[#9CA3AF]"><span className="text-[#6B7280]">Submitted:</span> {new Date(q.created_at).toLocaleString()}</p>
                        {q.message && <p className="text-[#9CA3AF] pt-2"><span className="text-[#6B7280]">Message:</span> {q.message}</p>}
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wider text-[#6B7280] mb-3">Update status</p>
                        <div className="flex gap-2 flex-wrap">
                          {["new", "contacted", "closed"].map((s) => (
                            <button key={s} onClick={() => updateStatus(q.id, s)}
                              className={`px-4 py-2 text-sm border rounded-sm transition-colors ${q.status === s ? statusColors[s] : "border-[#1F222E] text-[#9CA3AF] hover:border-[#3a3e4c]"}`}
                              data-testid={`status-${s}-${q.id}`}>{s}</button>
                          ))}
                          <a href={`mailto:${q.email}`} className="px-4 py-2 text-sm border border-[#1F222E] text-[#D4AF37] hover:border-[#D4AF37] rounded-sm" data-testid={`email-${q.id}`}>Reply by email</a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
